/**
 * Prefetching utilities for components, routes, and assets
 */

// Cache for prefetched modules
const prefetchCache = new Map<string, Promise<any>>();

/**
 * Prefetch a module (component, route, etc.)
 */
export function prefetchModule(importFn: () => Promise<any>): Promise<any> {
  const key = importFn.toString();
  
  if (prefetchCache.has(key)) {
    return prefetchCache.get(key)!;
  }
  
  const promise = importFn();
  prefetchCache.set(key, promise);
  return promise;
}

/**
 * Prefetch a route component
 */
export function prefetchRoute(routePath: string): void {
  // Map route paths to their import functions
  const routeMap: Record<string, () => Promise<any>> = {
    '/': () => import('@/pages/Home'),
    '/scooters': () => import('@/pages/Scooters'),
    '/clients': () => import('@/pages/Clients'),
    '/contracts': () => import('@/pages/Contracts'),
    '/finance': () => import('@/pages/Finance'),
    '/users': () => import('@/pages/Users'),
    '/user-actions': () => import('@/pages/UserActions'),
    '/management': () => import('@/pages/Management'),
    '/management/business-details': () => import('@/pages/BusinessDetails'),
    '/how-it-works': () => import('@/pages/HowItWorks'),
  };
  
  const importFn = routeMap[routePath];
  if (importFn) {
    prefetchModule(importFn).catch(() => {
      // Silently fail - prefetching is best effort
    });
  }
}

/**
 * Prefetch lucide-react icons that are commonly used
 */
export function prefetchIcons(): void {
  // Prefetch commonly used icons
  const commonIcons = [
    () => import('lucide-react').then(m => m.Search),
    () => import('lucide-react').then(m => m.Filter),
    () => import('lucide-react').then(m => m.Download),
    () => import('lucide-react').then(m => m.LayoutGrid),
    () => import('lucide-react').then(m => m.List),
    () => import('lucide-react').then(m => m.Map),
    () => import('lucide-react').then(m => m.KanbanSquare),
    () => import('lucide-react').then(m => m.Calendar),
    () => import('lucide-react').then(m => m.Globe),
  ];
  
  // Use requestIdleCallback if available, otherwise setTimeout
  const schedulePrefetch = (fn: () => void) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(fn, { timeout: 2000 });
    } else {
      setTimeout(fn, 100);
    }
  };
  
  schedulePrefetch(() => {
    commonIcons.forEach(iconFn => {
      prefetchModule(iconFn).catch(() => {});
    });
  });
}

/**
 * Prefetch critical components that are used across the app
 */
export function prefetchCriticalComponents(): void {
  const criticalComponents = [
    () => import('@/components/Toolbar'),
    () => import('@/components/Sidebar'),
    () => import('@/components/Buttons'),
    () => import('@/components/StatusBadge'),
    () => import('@/components/Drawer'),
  ];
  
  criticalComponents.forEach(componentFn => {
    prefetchModule(componentFn).catch(() => {});
  });
}

/**
 * Add resource hints (preload/prefetch) to the document head
 */
export function addResourceHint(href: string, as: string, rel: 'preload' | 'prefetch' = 'prefetch'): void {
  if (document.querySelector(`link[href="${href}"]`)) {
    return; // Already exists
  }
  
  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  link.as = as;
  document.head.appendChild(link);
}

/**
 * Initialize prefetching on app load
 */
export function initPrefetching(): void {
  // Prefetch critical components immediately
  prefetchCriticalComponents();
  
  // Prefetch icons in the background
  prefetchIcons();
  
  // Prefetch routes on mouseover/focus of navigation links
  if (typeof window !== 'undefined') {
    document.addEventListener('mouseover', (e) => {
      const target = e.target;
      // Check if target is an Element and has closest method
      if (target && typeof (target as any).closest === 'function') {
        const link = (target as Element).closest('a[href]') as HTMLAnchorElement;
        if (link && link.href.startsWith(window.location.origin)) {
          const path = new URL(link.href).pathname;
          prefetchRoute(path);
        }
      }
    }, { passive: true });
    
    document.addEventListener('focus', (e) => {
      const target = e.target;
      // Check if target is an Element and has closest method
      if (target && typeof (target as any).closest === 'function') {
        const link = (target as Element).closest('a[href]') as HTMLAnchorElement;
        if (link && link.href.startsWith(window.location.origin)) {
          const path = new URL(link.href).pathname;
          prefetchRoute(path);
        }
      }
    }, { passive: true, capture: true });
  }
}

